import { Col, Form, Row, Spin, notification } from "antd";
import { useHistory } from "react-router-dom";

import { withProcedureWraper } from "../../../..";
import {
  Box,
  Icon,
  DatePicker,
  DepartmentSelect,
  BoxSection,
  ApproverSelect,
  AlternateApproverSelect,
  ButtonList,
} from "../../../../../../components";

import { CoursesActionTable } from "../../components";
import {
  DateFormat,
  DepartmentCode,
  EmailType,
  EnvKey,
  RequestStatus,
  SystemCode,
} from "../../../../../../constants";
import { EnvHelper, StringHelper } from "../../../../../../helper";

import { IAPList, useEmployee, useSteps } from "../../../../../../hook";
import { useCreateMTP } from "../../hooks";
import { Routes } from "../../../../../../AppRoutes";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

const siteUrl = EnvHelper.getValue(EnvKey.APPROVER_MTP_URL);

const MTPCreate = withProcedureWraper(() => {
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const history = useHistory();
  const [eApprovedBy, setEApprovedBy] = useState("");

  const { getEmployeesByDepartmentCode } = useEmployee();
  const { getAvailableStep, sendMailAP } = useSteps({
    approved: EmailType.MTPA,
    rejected: EmailType.MTPR,
    completed: EmailType.MTPF,
  });
  const { mutate, isLoading } = useCreateMTP();

  const eDepartment = Form.useWatch("eDepartment", form);
  const departmentCode =
    StringHelper.verticalBarSplit<[DepartmentCode]>(eDepartment)[0];

  const onFinish = (values: any) => {
    const { initialize, empty } = getAvailableStep(SystemCode.MTP);
    const depCode = StringHelper.verticalBarSplit(values.eDepartment)[0];
    const assigneeTitle = ["2", "3"].includes(depCode)
      ? "Trưởng phòng KHHC"
      : "Thủ trưởng CQ/ĐV";
    const eApprovalProcess: IAPList = [
      [initialize],
      [
        {
          ...empty,
          title: assigneeTitle,
          name: StringHelper.verticalBarSplit(eApprovedBy)[1],
          email: StringHelper.verticalBarSplit(eApprovedBy)[0],
          requestStatus: RequestStatus.completed,
        },
      ],
    ];
    const body = {
      ...values,
      eApprovalProcess,
      eRequestStatus: RequestStatus.submitted,
    };
    mutate(body, {
      onSuccess(response: any) {
        queryClient.invalidateQueries({ queryKey: ["MTP-requests"] });
        sendMailAP(response.fields.eApprovalProcess, {
          toPath: `${siteUrl}/${response.id}`,
          type: RequestStatus.approved,
        });
        history.push(`${Routes.monthlyTrainingPlanPage.path}/${response.id}`);
        notification.success({
          message: "Thành công!",
          description: `Tạo yêu cầu thành công.`,
        });
      },
      onError() {
        notification.error({
          message: "Thất bại",
          description: `Tạo yêu cầu thất bại.`,
        });
      },
    });
  };

  const handleValuesChange = (changedValue: any = {}) => {
    const { eApprovedBy, eAlternateApprover } = changedValue;
    if (eApprovedBy || eAlternateApprover) {
      setEApprovedBy(eApprovedBy || eAlternateApprover);
    }
  };

  const employeesOptions = getEmployeesByDepartmentCode(departmentCode);

  return (
    <Spin spinning={isLoading}>
      <div className="pd15">
        <div className="PageHead flex-center">
          <div className="title" style={{ margin: 0 }}>
            <div className="btnBack" onClick={() => history.goBack()}>
              <Icon.ArrowLeft />
            </div>
            Tạo kế hoạch đào tạo tháng
          </div>
        </div>
        <Form
          form={form}
          size="large"
          layout="vertical"
          onFinish={onFinish}
          onValuesChange={handleValuesChange}
          initialValues={{ eTrainingCourses: [] }}
        >
          <Box title="Thông tin">
            <Row gutter={[32, 0]}>
              <Col span={12}>
                <Form.Item
                  required
                  label="Chọn tháng/năm"
                  name="eMonth"
                  rules={[
                    { required: true, message: "Vui lòng chọn tháng/năm!" },
                  ]}
                >
                  <DatePicker
                    format={DateFormat.MM_YYYY}
                    className="min-w-full"
                    picker="month"
                    placeholder="Chọn tháng..."
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  required
                  label="Cơ quan / Đơn vị"
                  name="eDepartment"
                  rules={[
                    {
                      required: true,
                      message: "Vui lòng chọn Cơ quan/Đơn vị!",
                    },
                  ]}
                >
                  <DepartmentSelect
                    className="min-w-full"
                    placeholder="Chọn cơ quan/đơn vị..."
                  />
                </Form.Item>
              </Col>
            </Row>
            <BoxSection title="Nội dung chi tiết">
              <Form.Item noStyle name="eTrainingCourses">
                <CoursesActionTable />
              </Form.Item>
            </BoxSection>
            <BoxSection title="Thông tin chi tiết">
              <Row gutter={[32, 0]}>
                <Col span={12} xs={24} md={12}>
                  <Form.Item
                    required
                    label="Người duyệt"
                    name="eApprovedBy"
                    rules={[
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (value || getFieldValue("eAlternateApprover")) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            new Error("Vui lòng chọn trường này!")
                          );
                        },
                      }),
                    ]}
                  >
                    <ApproverSelect
                      placeholder="Thủ trưởng CQ/ĐV"
                      options={employeesOptions as any}
                    />
                  </Form.Item>
                </Col>
                <Col span={12} xs={24} md={12}>
                  <Form.Item
                    label="Người duyệt thay thế"
                    name="eAlternateApprover"
                  >
                    <AlternateApproverSelect />
                  </Form.Item>
                </Col>
              </Row>
            </BoxSection>
          </Box>
          <div className="flex-end">
            <ButtonList
              items={[
                {
                  buttonType: "danger",
                  label: "Huỷ",
                  onClick: () => history.goBack(),
                },
                {
                  buttonType: "warning",
                  label: "Xác nhận",
                  type: "submit",
                },
              ]}
            />
          </div>
        </Form>
      </div>
    </Spin>
  );
});

export default MTPCreate;
